import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FiDownload } from "react-icons/fi";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../../hooks/useColumnVisibility";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../components/BaseUI/Table";
import Button from "../../../../components/BaseUI/Button";
import Lozenge from "../../../../components/BaseUI/Lozenge";
import Pagination from "../../../../components/BaseUI/Pagination";
import {
  SectionSpinner,
  SectionEmpty,
  fmtPct,
  fmtMins,
  oeeAppearance,
  toISO,
  exportXLSX,
} from "../shared/utils";
import { API_URL } from "../shared/constants";
import OEEReportShell from "./OEEReportShell";
import { useOEEReport } from "./useOEEReport";

const OEE_TABLE_COLUMNS = [
  "Makine",
  "Parça",
  "Çevrim Süresi",
  "Üretilen",
  "Duruş Sayısı",
  "Duruş Süresi",
  "Kullanılabilirlik %",
  "Performans %",
  "OEE %",
].map((name, id) => ({ name, id, sortable: false }));

const OEEByMachineReport = () => {
  useTitle("Rovion MES - Makine Bazlı Rapor");
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });

  const {
    filters,
    machines,
    shifts,
    teepData,
    loading,
    fetchData,
    toasts,
    dismissToast,
    authH,
  } = useOEEReport({
    autoFetch: false,
    machineBreakdownPage: pagination.page,
    machineBreakdownPageSize: pagination.pageSize,
  });

  const {
    visibleColumns,
    toggleColumnVisibility,
    resetToDefault,
  } = useColumnVisibility("oee-by-machine-list", OEE_TABLE_COLUMNS);

  const mb = teepData?.machineBreakdown || [];
  const mbMeta = teepData?.machineBreakdownPagination;

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!mbMeta) return;
    setPagination((prev) => ({
      ...prev,
      page: mbMeta.page ?? prev.page,
      pageSize: mbMeta.pageSize ?? prev.pageSize,
      total: mbMeta.total ?? 0,
      totalPages: mbMeta.totalPages ?? 0,
    }));
  }, [mbMeta]);

  const handleApply = useCallback(() => {
    if (pagination.page === 1) {
      fetchData({ machineBreakdownPage: 1 });
    } else {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [fetchData, pagination.page]);

  const handlePageChange = useCallback(({ pagination: pg }) => {
    setPagination((prev) => ({
      ...prev,
      page: pg.currentPage,
      pageSize: pg.itemsPerPage,
    }));
  }, []);

  const exportMachines = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.machineIds.length)
        p.set("machineIds", filters.machineIds.join(","));
      if (filters.shiftId) p.set("shiftId", filters.shiftId);
      p.set("machineBreakdownPage", "1");
      p.set("machineBreakdownPageSize", "500");

      const r = await axios.get(`${API_URL}/api/teep?${p}`, authH());
      const rows = r.data.data?.machineBreakdown || [];

      exportXLSX(
        rows.map((m) => ({
          Makine: m.name,
          Kod: m.code,
          Parça: m.partName || "—",
          "Parça Kodu": m.partCode || "—",
          "Çevrim Süresi": m.netCycleTimeSecs ?? "—",
          Üretilen: m.units,
          "Duruş Sayısı": m.stopCount,
          "Duruş Süresi (dk)": m.stopMinutes,
          "Kullanılabilir Süre (dk)": m.availableMinutes,
          "Kullanılabilirlik %": m.availability,
          "Performans %": m.performance ?? "—",
          "OEE %": m.oee ?? "—",
        })),
        `oee-makine-${filters.startDate}-${filters.endDate}`,
        "Makine OEE",
      );
    } catch {
      exportXLSX(
        mb.map((m) => ({
          Makine: m.name,
          Kod: m.code,
          Parça: m.partName || "—",
          "Parça Kodu": m.partCode || "—",
          "Çevrim Süresi": m.netCycleTimeSecs ?? "—",
          Üretilen: m.units,
          "Duruş Sayısı": m.stopCount,
          "Duruş Süresi (dk)": m.stopMinutes,
          "Kullanılabilir Süre (dk)": m.availableMinutes,
          "Kullanılabilirlik %": m.availability,
          "Performans %": m.performance ?? "—",
          "OEE %": m.oee ?? "—",
        })),
        `oee-makine-${filters.startDate}-${filters.endDate}`,
        "Makine OEE",
      );
    }
  }, [filters, authH, mb]);

  const content = () => {
    if (loading) return <SectionSpinner />;
    if (!teepData || !mb.length)
      return <SectionEmpty msg="Makine OEE verisi bulunamadı." />;

    return (
      <CardContainer
        title="Makine Bazlı OEE Dökümü"
        actions={
          <Button
            appearance="subtle"
            onClick={exportMachines}
            iconBefore={<FiDownload size={13} />}
          >
            Excel İndir
          </Button>
        }
      >
        <Table
          columns={OEE_TABLE_COLUMNS}
          visibleColumns={visibleColumns}
          search={false}
          hideFooter
        >
          {mb.map((m, i) => (
            <Tr key={m.machineId || i} data={m}>
              {visibleColumns.includes(0) && (
                <Td>
                  <strong>{m.name}</strong>{" "}
                  <span className="text-muted small">{m.code}</span>
                </Td>
              )}
              {visibleColumns.includes(1) && (
                <Td>
                  {m.partName || m.partCode ? (
                    <>
                      {m.partName || m.partCode}{" "}
                      {m.partName && m.partCode && (
                        <span className="text-muted small">{m.partCode}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(2) && (
                <Td>
                  {m.netCycleTimeSecs != null ? (
                    `${m.netCycleTimeSecs}s`
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(3) && (
                <Td>
                  <strong>{(m.units || 0).toLocaleString("tr-TR")}</strong>
                </Td>
              )}
              {visibleColumns.includes(4) && <Td>{m.stopCount}</Td>}
              {visibleColumns.includes(5) && (
                <Td>
                  <span className="rp-ref-rejected">
                    {fmtMins(m.stopMinutes)}
                  </span>
                </Td>
              )}
              {visibleColumns.includes(6) && (
                <Td>
                  <Lozenge appearance={oeeAppearance(m.availability)} isBold>
                    {fmtPct(m.availability)}
                  </Lozenge>
                </Td>
              )}
              {visibleColumns.includes(7) && (
                <Td>
                  {m.performance != null ? (
                    <Lozenge appearance={oeeAppearance(m.performance)} isBold>
                      {fmtPct(m.performance)}
                    </Lozenge>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(8) && (
                <Td>
                  {m.oee != null ? (
                    <Lozenge appearance={oeeAppearance(m.oee)} isBold>
                      {fmtPct(m.oee)}
                    </Lozenge>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Td>
              )}
            </Tr>
          ))}
        </Table>
        <Pagination
          totalItems={pagination.total}
          totalPerPage={pagination.pageSize}
          currentPage={pagination.page}
          onPageChange={handlePageChange}
        />
      </CardContainer>
    );
  };

  return (
    <OEEReportShell
      title="Makine Bazlı Rapor"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={handleApply}
      toasts={toasts}
      dismissToast={dismissToast}
      columns={OEE_TABLE_COLUMNS}
      visibleColumns={visibleColumns}
      onToggleColumn={toggleColumnVisibility}
      onResetColumns={resetToDefault}
    >
      {content()}
    </OEEReportShell>
  );
};

export default withAuth(OEEByMachineReport);
